import { Nullable, ObjectValues } from '@cast/leadcast-types';
import { useQuery } from '@tanstack/react-query';
import config from 'api/config';
import { BaseService, BaseServiceConstructor } from 'api/services/base.service';
import { plainToInstance } from 'class-transformer';
import { AuthUser, User } from 'features/customerPortal';
import router from 'next/router';
import qs from 'qs';
import { ApplicationFeature, TenantPublicWaterSystem } from 'types/models/admin/Tenant';
import { Private_Material } from 'types/models/inventory';
import { ClientContactDetails, PublicUser } from 'types/models/public';
import { PublicMapData } from 'types/models/public/PublicMapData';
import { PublicUserInfo } from 'types/models/public/PublicUserInfo';
import { TenantDetails } from 'types/models/public/TenantDetails';
import {
  Sample,
  SampleOrder,
  SamplingLocation,
  SamplingProgram,
  TapSampleForm,
  TapSampleFormSettings,
} from 'types/models/samples';
import { PublicUserRegistration } from 'types/requests/public/PublicUserRegistration';
import { AddressRecordSearch } from 'types/responses/AddressRecordSearch';
import { PublicROEFormsResponse, ROEResponse } from 'types/responses/ROEResponse';
import { ContactType, FEATURE_CONSTANTS, LicenseLevel } from 'utils/constants';

export class PublicService extends BaseService {
  constructor({ serviceUrls, token, tenantName }: BaseServiceConstructor) {
    super({ serviceUrls, token, tenantName });
  }

  async getClientDetails(): Promise<ClientContactDetails> {
    const { data } = await this.httpClient.get<ClientContactDetails>(`/Public/CustomerContact`);
    return data;
  }

  async getTenantDetails(): Promise<TenantDetails> {
    const { data } = await this.httpClient.get<TenantDetails>(`/Public/TenantDetails`);
    return data;
  }

  async getPublicMaterials(): Promise<Private_Material[]> {
    const { data } = await this.httpClient.get<Private_Material[]>(`/Public/Materials`);
    return data;
  }

  async getCustomerData(): Promise<PublicUser[]> {
    const { data } = await this.httpClient.get<PublicUser[]>(`/Applications/PublicUsers`);
    return data;
  }

  async getAddresses(registrationCode): Promise<AddressRecordSearch> {
    const { data } = await this.httpClient.get<AddressRecordSearch>(
      `/public/search/registrationCode/${registrationCode}`,
      {
        headers: {
          'Tenant-Name': router.query.tenantName,
        },
      }
    );
    return data;
  }

  async recoverAccount(emailAddress: string): Promise<void> {
    const queryString = qs.stringify({ emailAddress });
    await this.httpClient.get(`/public/recover?${queryString}`);
  }

  async verifyAccount(propertyId: string, utilityAccountNumber: string): Promise<void> {
    await this.httpClient.put(
      `/PublicUser/Properties/${propertyId}/Accounts/${utilityAccountNumber}`,
      {},
      {
        withCredentials: true,
        headers: {
          'Tenant-Name': router.query.tenantName,
        },
      }
    );
  }

  async getApplicationStatus(
    featureValue?: ObjectValues<typeof FEATURE_CONSTANTS>
  ): Promise<ApplicationFeature['options']> {
    const applicationFeature = featureValue ? featureValue : FEATURE_CONSTANTS.CUSTOMER_PORTAL;
    const { data } = await this.httpClient.get<ApplicationFeature['options']>(
      `/public/applicationStatus/${applicationFeature}`
    );
    return data;
  }

  async getTenantLicenseLevel(): Promise<LicenseLevel> {
    const { data } = await this.httpClient.get<LicenseLevel>(`/public/licenseLevel`);
    return data;
  }

  async updatePassword(
    token: string,
    payload: { password: string; confirmPassword: string }
  ): Promise<void> {
    const queryString = qs.stringify({ token });
    await this.httpClient.post(`/public/updatePassword?${queryString}`, payload);
  }

  async registerUser(payload: PublicUserRegistration): Promise<PublicUserRegistration> {
    const { data } = await this.httpClient.put<PublicUserRegistration>(`/public/signup`, payload);
    return data;
  }

  async login(payload: { email: string; password: string }): Promise<void> {
    const response = await this.httpClient.post(`/public/login`, payload, {
      headers: {
        'Tenant-Name': router.query.tenantName,
      },
      withCredentials: true,
    });
  }

  async searchUtility(searchFormValues: string): Promise<TenantPublicWaterSystem[]> {
    const { data } = await this.httpClient.post<TenantPublicWaterSystem[]>(
      `/public/search/utility`,
      searchFormValues ?? ''
    );
    return data;
  }

  async getPublicUser(): Promise<User> {
    const { data } = await this.httpClient.get<User>(`/PublicUser/Info`, {
      headers: {
        'Tenant-Name': router.query.tenantName,
      },
      withCredentials: true,
    });
    return data;
  }

  async publicUserLogout(): Promise<void> {
    await this.httpClient.get(`/PublicUser/Logout`, {
      headers: {
        'Tenant-Name': router.query.tenantName,
      },
      withCredentials: true,
    });
  }

  async getHelpPublicUser(body: {
    Name: string;
    EmailAddress: string;
    PhoneNumber: string;
    Message: string;
    PropertyId: string;
    ContactMethod: 'email' | 'phone';
  }): Promise<void> {
    await this.httpClient.post(`/PublicUser/Help`, body, {
      headers: {
        'Tenant-Name': router.query.tenantName,
      },
      withCredentials: true,
    });
  }

  async requestVerificationCode(emailAddress: string): Promise<void> {
    const queryString = qs.stringify({ emailAddress });
    await this.httpClient.get(`/public/verificationEmail?${queryString}`, {
      headers: {
        'Tenant-Name': router.query.tenantName,
      },
      withCredentials: true,
    });
  }

  async validateRegistrationCode(
    selectedPropertyId: string,
    registrationCode: string
  ): Promise<void> {
    await this.httpClient.post(`/public/validate/registrationCode`, {
      propertyId: selectedPropertyId,
      registrationCode,
    });
  }

  async validatePropertyWithTapSampleForm(
    selectedPropertyId: string,
    tapSampleFormId: string
  ): Promise<void> {
    await this.httpClient.post(`/public/validate/property`, {
      propertyId: selectedPropertyId,
      tapSampleFormId: tapSampleFormId,
    });
  }

  async submitMaterialReport(body): Promise<void> {
    await this.httpClient.post(`/PublicUser/MaterialReport`, body, {
      headers: {
        'Tenant-Name': router.query.tenantName,
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
    });
  }

  async removePropertiesRequest(propertyIds: string[]): Promise<void> {
    await this.httpClient.post(
      `/PublicUser/RemoveProperties`,
      {
        propertyIds,
      },
      {
        headers: {
          'Tenant-Name': router.query.tenantName,
        },
        withCredentials: true,
      }
    );
  }

  // TODO: Response is unused for now but should type eventually
  async updatePublicAccount(payload: {
    email: string;
    phoneNumber: string;
    mailingAddress: string;
    address?: string;
    addressUnit?: string;
    city?: string;
    county?: string;
    state?: string;
    zipCode?: string;
    contactPreference: string;
  }) {
    const { data } = await this.httpClient.post(`/PublicUser/UpdateAccount`, payload, {
      headers: {
        'Tenant-Name': router.query.tenantName,
      },
      withCredentials: true,
    });
    return data;
  }

  async addPublicProperty(
    payload: {
      accountNumber: Nullable<string>;
      contactType: ContactType;
      isResident: 'Yes' | 'No';
    },
    propertyId: string
  ): Promise<PublicUserInfo> {
    const { data } = await this.httpClient.post<PublicUserInfo>(
      `/PublicUser/AddProperty`,
      {
        accountNumber: payload.accountNumber,
        propertyId,
        contactType: payload.contactType,
        isResident: payload.isResident === 'Yes',
      },
      {
        headers: {
          'Tenant-Name': router.query.tenantName,
        },
        withCredentials: true,
      }
    );
    return data;
  }

  async getPropertyDetailsById(propertyId: string): Promise<PublicMapData[]> {
    const { data } = await this.httpClient.get<PublicMapData[]>(
      `/Public/PropertyDetails/${propertyId}`,
      {
        headers: {
          'Tenant-Name': router.query.tenantName,
          'Content-Type': 'application/json',
        },
      }
    );
    return data;
  }

  async validatePublicPassword(passcode: string, emailAddress: string): Promise<void> {
    await this.httpClient.post(
      `/public/validate/passcode`,
      {
        passcode: passcode.trim(),
        emailAddress,
      },
      {
        headers: {
          'Tenant-Name': router.query.tenantName,
        },
        withCredentials: true,
      }
    );
  }

  async getPublicROEForms(): Promise<PublicROEFormsResponse> {
    const { data } = await this.httpClient.get<PublicROEFormsResponse>(
      `/PublicUser/Forms/RightofEntry`,
      {
        headers: {
          'Tenant-Name': router.query.tenantName,
        },
        withCredentials: true,
      }
    );
    return data;
  }

  async submitPublicROE(
    propertyIds: string[],
    formId: string,
    formMethod: string,
    formText: ROEResponse,
    payload: {
      isApproved: string;
      formSignatureImage: string;
    },
    userInfo: AuthUser,
    bucketLocation: string
  ): Promise<void> {
    await this.httpClient.post(
      `/PublicUser/RightOfEntry`,
      {
        propertyIds,
        formId,
        method: formMethod,
        formText: JSON.stringify(formText),
        isApproved: payload.isApproved === 'yes',
        fileName: `${userInfo.firstName}.png`,
        fileUrl: null,
        fileBucketLocation: bucketLocation,
        fileMimeType: 'image/png',
        fileString: payload.formSignatureImage,
      },
      {
        headers: {
          'Tenant-Name': router.query.tenantName,
        },
        withCredentials: true,
      }
    );
  }

  async getPublicWaterSampleLocations(translatedFields = ['name']): Promise<SamplingLocation[]> {
    const { data } = await this.httpClient.get<SamplingLocation[]>(
      `/PublicUser/TapSampleLocations`,
      {
        headers: {
          'Tenant-Name': router.query.tenantName,
        },
        params: {
          locale: router.locale,
          translatedFields: translatedFields.join(','),
        },
        withCredentials: true,
      }
    );
    return data;
  }

  async getPublicWaterSampleConfiguration(): Promise<TapSampleFormSettings> {
    const { data } = await this.httpClient.get<TapSampleFormSettings>(
      `/PublicUser/TapSampleFormSettings`,
      {
        headers: {
          'Tenant-Name': router.query.tenantName,
        },
        withCredentials: true,
      }
    );
    return data;
  }

  async getPublicSampleFormById(sampleReportId: string): Promise<string> {
    const { data } = await this.httpClient.get<string>(
      `/PublicUser/search/tapsampleformid/${sampleReportId}`,
      {
        headers: {
          'Tenant-Name': router.query.tenantName,
        },
        withCredentials: true,
      }
    );
    return data;
  }

  async getUserSampleOrders(): Promise<SampleOrder[]> {
    const { data } = await this.httpClient.get<SampleOrder[]>(`/PublicUser/SampleOrders`, {
      headers: {
        'Tenant-Name': router.query.tenantName,
      },
      withCredentials: true,
    });
    return data.map((so) => plainToInstance(SampleOrder, so));
  }

  async getSamplingProgramsByPropertyId(
    propertyId: string,
    translatedFields = ['samplingKits.name']
  ): Promise<SamplingProgram[]> {
    const { data } = await this.httpClient.get<SamplingProgram[]>(
      `/PublicUser/EligibleSamplingPrograms/${propertyId}`,
      {
        headers: {
          'Tenant-Name': router.query.tenantName,
        },
        params: {
          locale: router.locale,
          translatedFields: translatedFields.join(','),
        },
        withCredentials: true,
      }
    );
    return data;
  }

  async getWaterSampleFormById(formId: string): Promise<TapSampleForm> {
    const { data } = await this.httpClient.get<TapSampleForm>(`/TapSampleForm/${formId}`, {
      headers: {
        'Tenant-Name': router.query.tenantName,
      },
      withCredentials: true,
    });
    return data;
  }

  // TODO: Correctly type multi-part form request body
  async submitWaterSampleForm(form: FormData, id: string, filename: string): Promise<void> {
    await this.httpClient.put<void>(`/PublicUser/TapSampleForm/${id}?filename=${filename}`, form, {
      withCredentials: true,
      headers: {
        'Tenant-Name': router.query.tenantName,
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async getPublicUserByPropertyId(propertyId: string): Promise<PublicUser> {
    const queryParams = qs.stringify({ propertyId });
    const { data } = await this.httpClient.get<PublicUser>(`/PublicUser/User/?${queryParams}`, {
      withCredentials: false,
    });
    return data;
  }

  async getTenantAdminEmails(): Promise<{ emails: string[] }> {
    const { data } = await this.httpClient.get<{ emails: string[] }>(`/PublicUser/Admin`, {
      withCredentials: false,
    });
    return data;
  }

  getCachedTenantAdminEmails() {
    return useQuery(
      ['AdminEmails'],
      async () => {
        const { emails } = await this.getTenantAdminEmails();
        return emails;
      },
      {
        cacheTime: config.cacheTime,
        staleTime: config.cacheTime,
        placeholderData: [],
      }
    );
  }

  async sendInspectionResubmittalEmail(inspectionId: string): Promise<void> {
    await this.httpClient.get(`/Inventory/InspectionResubmittalEmail/${inspectionId}`);
  }

  async refreshPublicPortalSession(): Promise<void> {
    await this.httpClient.put(
      `/PublicUser/Session`,
      {},
      {
        headers: {
          'Tenant-Name': router.query.tenantName,
        },
        withCredentials: true,
      }
    );
  }
}
